import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Inject, InjectionToken, Input, OnInit, Output, Renderer2, ViewChild } from '@angular/core';
import { DateRange as CalendarDateRange, DefaultMatCalendarRangeStrategy, MatCalendar, MatRangeDateSelectionModel } from '@angular/material/datepicker';
import { DateTime } from 'luxon';
import { CalendarService } from 'src/app/shared/services/calendar.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DateHelper, DateRangeFormat } from '../../../helpers/date.helper';
import { DateRange } from '../../../types/date-range.type';
import { DropdownComponent } from '../../dropdown/dropdown.component';
import { CalendarHeaderComponent } from '../calendar-header/calendar-header.component';
import { CalendarShortcut } from './calendar-shortcut.type';

export const CALENDAR_CONFIG = new InjectionToken<CalendarConfig>('CALENDAR_CONFIG');

export interface CalendarConfig {
  showTodayButton?: boolean;
  showYesterdayButton?: boolean;
  show7DaysButton?: boolean;
  show30DaysButton?: boolean;
}

@Component({
  selector: 'shared-calendar-interval-dropdown',
  templateUrl: './calendar-interval-dropdown.component.html',
  styleUrls: ['./calendar-interval-dropdown.component.scss'],
  providers: [DefaultMatCalendarRangeStrategy, MatRangeDateSelectionModel],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CalendarIntervalDropdownComponent implements OnInit {
  @Input() disabled: boolean;
  @Input() showShortcuts = true;
  @ViewChild(DropdownComponent, { static: false }) childRef: DropdownComponent;
  @ViewChild(MatCalendar, { static: false }) calendar: MatCalendar<DateTime>;

  readonly customCalendarHeader = CalendarHeaderComponent;
  readonly shortcuts: CalendarShortcut[] = [
    new CalendarShortcut(0, 0, 'Today'),
    new CalendarShortcut(1, 1, 'Yesterday'),
    new CalendarShortcut(7, 1, 'Last 7 days'),
    new CalendarShortcut(30, 1, 'Last 30 days'),
  ];

  @Output() selectedRangeChange = new EventEmitter<DateRange>();

  cells!: { date: number; element: HTMLElement; change: boolean; listen?: () => void }[];
  dateOver: any;
  calendarEditingDateRange = false;
  private _selectedDateRange: CalendarDateRange<DateTime>;

  get selectedRangeLabel(): string {
    return this.calendarService.dateRange.format(DateRangeFormat.DateOnly);
  }

  get selectedDateRange(): CalendarDateRange<DateTime> {
    return this._selectedDateRange;
  }

  get startAt(): DateTime {
    return DateTime.fromJSDate(this.calendarService.dateRange.start);
  }

  get minDate(): DateTime {
    return DateTime.fromMillis(0);
  }

  constructor(
    @Inject(CALENDAR_CONFIG) public config: CalendarConfig,
    private renderer: Renderer2,
    private readonly selectionModel: MatRangeDateSelectionModel<DateTime>,
    private readonly selectionStrategy: DefaultMatCalendarRangeStrategy<DateTime>,
    private cdr: ChangeDetectorRef,
    private calendarService: CalendarService,
  ) {
    this.calendarService.dateRangeChanged$.pipe(takeUntilDestroyed()).subscribe(d => this.onSelectedDateRangeChanged(d));
    this.calendarService.dataAvailabilityChanged$.pipe(takeUntilDestroyed()).subscribe(d => this.cdr.markForCheck());
  }

  ngOnInit(): void {
    this.onSelectedDateRangeChanged(this.calendarService.dateRange);
  }

  private onSelectedDateRangeChanged(dateRange: DateRange): void {
    if (!dateRange?.isValid()) {
      this._selectedDateRange = null;
      return;
    }
    this._selectedDateRange = dateRange != null ? new CalendarDateRange<DateTime>(DateTime.fromJSDate(dateRange.start), DateTime.fromJSDate(dateRange.end)) : null;
    this.shortcuts.forEach(x => x.selected = this.calendarService.dateRange?.equals(x.getSelectionInterval()) ?? false);
    this.cdr.markForCheck();
  }

  setClass() {
    return (date: DateTime): string => {
      // is used to reset cells after month change
      if (date.day === 1) {
        this.setCells();
      }
      if (this.calendar?.currentView == 'year') {
        const monthStart = date.startOf('month').toJSDate();
        const monthEnd = date.endOf('month').toJSDate();
        if (!(this.calendarService.dataAvailability?.hasAnyDataForInterval(new DateRange(monthStart, monthEnd)) ?? false)) {
          return 'no-data';
        }
      } else {
        if (!(this.calendarService.dataAvailability?.hasDataForDate(date.toJSDate()) ?? false)) {
          return 'no-data';
        }
      } 
      return '';
    };
  }

  async setRangeManually(dateRange: CalendarShortcut): Promise<void> {
    const selectedDateRange = dateRange.getSelectionInterval();
    const calendarRange = new CalendarDateRange<DateTime>(DateTime.fromJSDate(selectedDateRange.start), DateTime.fromJSDate(selectedDateRange.end));
    this.selectionModel.updateSelection(calendarRange, this);
    await this.calendarService.selectDateRange(selectedDateRange);
    this.childRef?.closed.emit();
  }

  async selectedChanged(selectedDate: DateTime): Promise<void> {
    const selection = this.selectionModel.selection;
    const newSelection = this.selectionStrategy.selectionFinished(selectedDate, selection);
    this.selectionModel.updateSelection(newSelection, this);
    this._selectedDateRange = new CalendarDateRange<DateTime>(newSelection.start, newSelection.end ?? newSelection.start);

    // start listening for mouseover events on cells if selection is started
    if (newSelection.start != null && newSelection.end === null) {
      this.calendarEditingDateRange = true;
      this.redrawCells(null); // reset cells
    } else if (newSelection.start != null && newSelection.end != null) {
      this.calendarEditingDateRange = false;
      await this.calendarService.selectDateRange(new DateRange(newSelection.start.toJSDate(), newSelection.end?.toJSDate() ?? newSelection.start.toJSDate()));
      this.childRef?.closed.emit();
    }
  }

  async onClose(): Promise<void> {
    if (this.calendarEditingDateRange) {
      this.calendarEditingDateRange = false;
      const selection = this.selectionModel.selection;
      const newSelection = this.selectionStrategy.selectionFinished(selection.start, selection);
      this.selectionModel.updateSelection(newSelection, this);
      await this.calendarService.selectDateRange(new DateRange(this._selectedDateRange.start.toJSDate()));
      this.redrawCells(null); // reset cells
    }
    this.calendar.activeDate = this.startAt;
  }

  viewChanged(event: any): void {
    // reset cells '.mat-calendar-body-active' class on view change
    const elements = document.querySelectorAll('.calendar');
    if (!elements || elements.length == 0) return;
    const cells = elements[0].querySelectorAll('.mat-calendar-body-active');
    cells?.forEach((x, index) => {
      this.renderer.removeClass(x, 'mat-calendar-body-active');
    });
  }

  private setCells(): void {
    setTimeout(() => {
      if (this.cells) {
        this.cells.forEach(x => {
          x.listen();
        });
      }
      this.dateOver = null;
      const elements = document.querySelectorAll('.calendar');
      if (!elements || elements.length == 0) return;
      const cells = elements[0].querySelectorAll('.mat-calendar-body-cell:not(.mat-calendar-body-disabled');
      this.cells = [];
      cells.forEach((x, index) => {
        const str = x.getAttribute('aria-label');
        const date = new Date(str);
        const time = new Date(date.getFullYear() + '-' + (date.getMonth() + 1) + '-' + date.getDate()).getTime();
        this.cells.push({
          date: time,
          element: x as HTMLElement,
          change: time >= this.selectedDateRange.start.valueOf() && time <= this.selectedDateRange.end.valueOf(),
        });
      });
      this.cells.forEach(x => {
        if (!x.listen) {
          x.listen = this.renderer.listen(x.element, 'mouseover', () => {
            if (!this.selectedDateRange.start?.millisecond && this.dateOver != x.date) {
              this.dateOver = x.date;
              this.redrawCells(this.dateOver as number);
            }
          });
        }
      });
    }, 0);
  }

  private redrawCells(timeTo: number): void {
    timeTo = timeTo || this.selectedDateRange.end?.valueOf();
    if (timeTo < this.selectedDateRange.start?.valueOf()) timeTo = this.selectedDateRange.start?.valueOf();
    if (!this.cells) {
      this.setCells();
    }

    this.cells?.forEach(x => {
      const change = this.calendarEditingDateRange ? this.selectedDateRange.start?.valueOf() && x.date >= this.selectedDateRange.start?.valueOf() && x.date <= timeTo : false;
      if (change || x.change) {
        x.change = change;
        const addInside = x.change ? 'addClass' : 'removeClass';
        const addFrom = DateHelper.isSameDay(new Date(x.date), this.selectedDateRange.start.toJSDate()) || (x.date == timeTo && DateHelper.isSameDay(new Date(timeTo), this.selectedDateRange.start.toJSDate())) ? 'addClass' : 'removeClass';
        const addTo = DateHelper.isSameDay(new Date(x.date), new Date(timeTo)) || (x.date == this.selectedDateRange.start?.valueOf() && this.selectedDateRange.start?.valueOf() == timeTo) ? 'addClass' : 'removeClass';

        this.renderer[addInside](x.element, 'inside');
        this.renderer[addFrom](x.element, 'from');
        this.renderer[addTo](x.element, 'to');
      }
    });
  }
}
